import { ApolloLink, NormalizedCacheObject } from '@apollo/client'
import { NextPageContext } from 'next'
import { useMemo } from 'react'

import { createApiApolloLink } from './apollo-client-api'
import { createPrivateApolloLink, createPrivateWebSocketLink } from './apollo-client-private'
import { createPublicCompanyApolloLink } from './apollo-client-public-company'
import { createPublicLaunchApolloLink } from './apollo-client-public-launch'
import clientSideApollo from './clientSideApollo'
import createApolloClient from './createApolloClient'
import {
  isPublicPagePathname,
  testUsePublicCompanyApolloLink,
  testUsePublicLaunchApolloLink,
  testUseWebSocketLink,
} from './utils'

import { createPublicPagesApolloLink } from '~apollo/apollo-client-public-pages'
import { isBrowser } from '~lib/helpers'

/**
 * Creates apollo client instance to use in client/server sides app
 */
export const initApolloClient = (initialState: Record<string, any>, ctx: NextPageContext | null = null) => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!isBrowser()) {
    return createApolloClient(
      initialState,
      ApolloLink.split(
        testUsePublicCompanyApolloLink,
        createPublicCompanyApolloLink(ctx),
        ApolloLink.split(
          () => ctx?.pathname && isPublicPagePathname(ctx.pathname),
          createPublicPagesApolloLink(ctx),
          ApolloLink.split(
            testUsePublicLaunchApolloLink,
            createPublicLaunchApolloLink(ctx),
            createPrivateApolloLink(ctx)
          )
        )
      )
    )
  }

  // Reuse client on the client-side
  if (!clientSideApollo.exists) {
    clientSideApollo.apollo = createApolloClient(
      initialState,
      ApolloLink.split(
        testUsePublicCompanyApolloLink,
        createPublicCompanyApolloLink(ctx),
        ApolloLink.split(
          () => isPublicPagePathname(window.location.pathname),
          createPublicPagesApolloLink(ctx),
          ApolloLink.split(
            testUsePublicLaunchApolloLink,
            createPublicLaunchApolloLink(ctx),
            ApolloLink.split(testUseWebSocketLink, createPrivateWebSocketLink(), createPrivateApolloLink(ctx))
          )
        )
      )
    )
  }

  return clientSideApollo.apollo
}

/**
 * Creates apollo client instance to use on server
 */
export const initApolloApi = () => {
  return createApolloClient(null, createApiApolloLink())
}

/**
 * Hook to handle client side apollo initialization
 */
export const useInitApollo = (apolloState: NormalizedCacheObject) => {
  return useMemo(() => initApolloClient(apolloState), [apolloState])
}
